// src/store/TransactionSlice.js
import {
  createSlice,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { supabase } from "services/supabase";
import moment from "moment";

// ------------------------------------------------------------------------
// Initial state
const initialState = {
  transactions: [],
  loading: false,
  error: null,
  transactionsLoaded: false,
};

// ------------------------------------------------------------------------
// Async Thunks

let lastRefresh = 0;

export const fetchSupabaseTransactions = createAsyncThunk(
  "data/fetchSupabaseTransactions",
  async ({ userId, client_id } = {}, { getState, rejectWithValue }) => {
    try {
      if (lastRefresh > 0 && moment().unix() - lastRefresh < 3) {
        lastRefresh = 0;
      }

      if (!userId) {
        throw new Error("User not authenticated");
      }

      // Build Supabase query for transactions
      let query = supabase
        .from("financial_transactions")
        .select("*")
        .eq("uid", userId);

      // Apply client_id filter if provided
      if (client_id && client_id !== "" && client_id !== null) {
        const clientId = typeof client_id === 'string' ? client_id : String(client_id);
        query = query.eq("client_id", clientId);
      }

      // Order by effective_date desc (newest first)
      query = query.order("effective_date", { ascending: false, nullsFirst: false });
      
      const { data: transactions, error } = await query;
      
      if (error) {
        console.error("fetchSupabaseTransactions: Query error:", error);
        throw error;
      }

      // Get unique invoice IDs from transactions
      const invoiceIds = [...new Set(
        (transactions || [])
          .map(t => t.invoice_id)
          .filter(id => id != null && id !== "")
      )];

      // Fetch invoices if we have any invoice IDs
      let invoiceMap = {};
      if (invoiceIds.length > 0) {
        const { data: invoices, error: invoiceError } = await supabase
          .from("invoices")
          .select("id, name, invoice_num")
          .in("id", invoiceIds)
          .eq("uid", userId);

        if (invoiceError) {
          console.warn("fetchSupabaseTransactions: Error fetching invoices:", invoiceError);
          // Continue without invoice data rather than failing
        } else {
          // Create a map of invoice_id -> invoice data
          invoiceMap = (invoices || []).reduce((acc, invoice) => {
            acc[invoice.id] = invoice;
            return acc;
          }, {});
        }
      }

      // Join transactions with invoices
      const transactionsWithInvoices = (transactions || []).map(transaction => ({
        ...transaction,
        invoice: transaction.invoice_id ? invoiceMap[transaction.invoice_id] : null,
      }));

      console.log(
        "fetchSupabaseTransactions: Query successful, returned",
        transactionsWithInvoices?.length || 0,
        "transactions"
      );
      
      lastRefresh = moment().unix();
      return transactionsWithInvoices || [];
    } catch (error) {
      console.error("fetchSupabaseTransactions: Caught error", error);
      return rejectWithValue(error.message);
    }
  }
);

export const createSupabaseTransaction = createAsyncThunk(
  "data/createSupabaseTransaction",
  async ({ userId, ...transactionData }, { rejectWithValue }) => {
    try {
      if (!userId) {
        throw new Error("User not authenticated");
      }

      // Validate required fields
      if (!transactionData.client_id) {
        throw new Error("Client ID is required");
      }
      if (transactionData.amount === undefined || transactionData.amount === null) {
        throw new Error("Amount is required");
      }
      if (!transactionData.effective_date) {
        throw new Error("Effective date is required");
      }

      // Prepare transaction data
      const data = {
        ...transactionData,
        uid: userId, // Explicitly set uid for RLS policy
      };

      // Remove any empty or undefined fields
      Object.keys(data).forEach((key) => {
        if (data[key] === "" || data[key] === undefined) {
          delete data[key];
        }
      });

      console.log("createSupabaseTransaction: Prepared transaction data:", data);

      const { data: result, error } = await supabase
        .from("financial_transactions")
        .insert(data)
        .select()
        .single();

      if (error) {
        console.error("createSupabaseTransaction: Supabase error details:", {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        });
        throw error;
      }

      console.log("createSupabaseTransaction: Transaction created successfully:", result);
      return result;
    } catch (error) {
      console.error("createSupabaseTransaction: Caught error", error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateSupabaseTransaction = createAsyncThunk(
  "data/updateSupabaseTransaction",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      console.log("updateSupabaseTransaction: Updating transaction", id, "with data:", data);

      const updateData = {
        ...data,
        updated_at: moment().toISOString(),
      };

      // Remove any empty or undefined fields
      Object.keys(updateData).forEach((key) => {
        if (updateData[key] === "" || updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      const { data: result, error } = await supabase
        .from("financial_transactions")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("updateSupabaseTransaction: Supabase error", error);
        throw error;
      }

      console.log("updateSupabaseTransaction: Transaction updated successfully:", result);
      return result;
    } catch (error) {
      console.error("updateSupabaseTransaction: Caught error", error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSupabaseTransaction = createAsyncThunk(
  "data/deleteSupabaseTransaction",
  async ({ id, userId }, { rejectWithValue }) => {
    try {
      if (!id) {
        throw new Error("Transaction ID is required");
      }
      if (!userId) {
        throw new Error("User not authenticated");
      }

      const { error } = await supabase
        .from("financial_transactions")
        .delete()
        .eq("id", id)
        .eq("uid", userId);

      if (error) {
        console.error("deleteSupabaseTransaction: Supabase error", error);
        throw error;
      }

      return { id };
    } catch (error) {
      console.error("deleteSupabaseTransaction: Caught error", error);
      return rejectWithValue(error.message);
    }
  }
);

// ------------------------------------------------------------------------
// Selectors
export const selectTransactionsLoaded = (state) =>
  state.transactionReducer.transactionsLoaded;
export const selectTransactions = (state) => state.transactionReducer.transactions;

// ------------------------------------------------------------------------
// Slice

export const TransactionSlice = createSlice({
  name: "transaction",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    // Realtime subscription actions
    addTransaction: (state, action) => {
      // Check if transaction already exists (prevent duplicates from realtime events)
      const existingIndex = state.transactions.findIndex(
        (trans) => trans.id === action.payload.id
      );
      if (existingIndex === -1) {
        // Add to beginning of array (newest first, matching fetch order)
        state.transactions.unshift(action.payload);
      }
    },
    updateTransaction: (state, action) => {
      if (!action.payload || !action.payload.id) {
        console.warn('updateTransaction: Invalid payload, missing id', action.payload);
        return;
      }

      const transactionId = action.payload.id;
      const index = state.transactions.findIndex(
        (transaction) => transaction.id === transactionId
      );
      
      if (index !== -1) {
        // Update existing transaction in the list
        state.transactions[index] = { ...state.transactions[index], ...action.payload };
      } else {
        // Transaction not in list, add it (could be from realtime update)
        state.transactions.unshift(action.payload);
      }
    },
    removeTransaction: (state, action) => {
      // Remove from transactions array
      state.transactions = state.transactions.filter(
        (transaction) => transaction.id !== action.payload.id
      );
    },
    clearTransactions: (state) => {
      state.transactions = [];
      state.transactionsLoaded = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ------------------------------------------------------------
      .addCase(fetchSupabaseTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // ------------------------------------------------------------
      .addCase(fetchSupabaseTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload || [];
        state.transactionsLoaded = true;
        state.error = null;
      })
      // ------------------------------------------------------------
      .addCase(fetchSupabaseTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch transactions";
        state.transactionsLoaded = false;
      })
      // ------------------------------------------------------------
      .addCase(createSupabaseTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // ------------------------------------------------------------
      .addCase(createSupabaseTransaction.fulfilled, (state, action) => {
        state.loading = false;
        // Add new transaction to the beginning of the array (newest first)
        state.transactions.unshift(action.payload);
        state.error = null;
      })
      // ------------------------------------------------------------
      .addCase(createSupabaseTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create transaction";
      })
      // ------------------------------------------------------------
      .addCase(updateSupabaseTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // ------------------------------------------------------------
      .addCase(updateSupabaseTransaction.fulfilled, (state, action) => {
        state.loading = false;
        // Update transaction in the array
        const index = state.transactions.findIndex(
          (transaction) => transaction.id === action.payload.id
        );
        if (index !== -1) {
          state.transactions[index] = action.payload;
        }
        state.error = null;
      })
      // ------------------------------------------------------------
      .addCase(updateSupabaseTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update transaction";
      })
      .addCase(deleteSupabaseTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSupabaseTransaction.fulfilled, (state, action) => {
        state.loading = false;
        const removedId = String(action.payload.id);
        state.transactions = state.transactions.filter(
          (transaction) => String(transaction.id) !== removedId
        );
        state.error = null;
      })
      .addCase(deleteSupabaseTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete transaction";
      });
  },
});

export const {
  setLoading,
  setError,
  addTransaction,
  updateTransaction,
  removeTransaction,
  clearTransactions,
} = TransactionSlice.actions;

export default TransactionSlice.reducer;
